/* Knurlhouse rating breakdown above the Judge.me review widget (Free plan).
   Counts come live from Judge.me's own widget feed (the `histogram` in cdn.judge.me
   reviews_for_widget, the same feed the widget loads); nothing is stored or hardcoded.
   Each row drives Judge.me's built-in rating filter: it opens the widget's filter panel and
   picks that rating (closing the panel would clear the filter, so the panel stays open but its
   duplicate chips are hidden). Pressing the active row again uses Judge.me's "Clear filters".
   The feed and the filter markup are undocumented: if either is missing or looks wrong, the
   breakdown is never shown and the widget is left alone.
   It also keeps the pinned reviews in view: Judge.me's autoplay is held paused, and the slider is
   rewound after a row filter is applied or cleared (Judge.me keeps the old scroll position).
   Star-only ratings show without the product title Judge.me puts in as their text. A rating row
   never combines with the "Only Pictures" sort, and the photo strip loses its arrows when every
   photo already fits. */
(() => {
  const FEED = 'https://cdn.judge.me/reviews/reviews_for_widget';
  const WAIT_MS = 15000;
  const FETCH_MS = 6000;

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  async function until(test, ms = 3000, step = 100) {
    for (let t = 0; t <= ms; t += step) {
      const v = test();
      if (v) return v;
      await sleep(step);
    }
    return null;
  }

  async function loadHistogram(productId) {
    const shop = window.Shopify && window.Shopify.shop;
    if (!shop) return null;
    const url = `${FEED}?product_id=${encodeURIComponent(productId)}&page=1&per_page=1&shop_domain=${encodeURIComponent(shop)}&platform=shopify`;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), FETCH_MS);
    try {
      const res = await fetch(url, { signal: ctrl.signal, credentials: 'omit' });
      if (!res.ok) return null;
      const data = await res.json();
      const rows = Array.isArray(data && data.histogram) ? data.histogram : null;
      if (!rows || rows.length !== 5) return null;
      const counts = {};
      for (const row of rows) {
        const rating = Number(row && row.rating);
        const n = Number(row && row.frequency);
        if (![1, 2, 3, 4, 5].includes(rating) || !Number.isInteger(n) || n < 0 || rating in counts) return null;
        counts[rating] = n;
      }
      const total = [5, 4, 3, 2, 1].reduce((s, r) => s + counts[r], 0);
      const stated = Number(data.number_of_reviews);
      if (total === 0 || (Number.isFinite(stated) && stated !== total)) return null;
      return { counts, total };
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  function filtersButton(widget) {
    return [...widget.querySelectorAll('button[aria-label]')].find((b) => b.getAttribute('aria-label').trim() === 'Filters') || null;
  }
  const panelOpen = (widget) => {
    const b = filtersButton(widget);
    return !!b && b.getAttribute('aria-expanded') === 'true';
  };

  function build(hist) {
    const box = document.createElement('div');
    box.className = 'kh-rbars';
    box.setAttribute('role', 'group');
    box.setAttribute('aria-label', 'Rating breakdown. Choose a rating to filter the reviews.');
    const max = Math.max(...[5, 4, 3, 2, 1].map((r) => hist.counts[r]));
    for (const r of [5, 4, 3, 2, 1]) {
      const n = hist.counts[r];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kh-rbars__row';
      btn.dataset.rating = String(r);
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', `${r} star${r === 1 ? '' : 's'}: ${n} review${n === 1 ? '' : 's'}. Show only ${r}-star reviews`);
      if (n === 0) btn.disabled = true;
      btn.innerHTML =
        `<span class="kh-rbars__label" aria-hidden="true">${r}<span class="kh-rbars__star">★</span></span>` +
        `<span class="kh-rbars__track" aria-hidden="true"><span class="kh-rbars__fill" style="width:${max ? (n / max) * 100 : 0}%"></span></span>` +
        `<span class="kh-rbars__count" aria-hidden="true">${n}</span>`;
      box.appendChild(btn);
    }
    const live = document.createElement('span');
    live.className = 'kh-rbars__live';
    live.setAttribute('aria-live', 'polite');
    box.appendChild(live);
    return box;
  }

  /* Judge.me's review slider auto-advances every 5 s, even off screen, so shoppers rarely saw the
     pinned reviews; the speed/autoplay setting is Awesome-plan only. Judge.me pauses autoplay
     while the mouse is over the slider (mouseenter) and resumes on mouseleave, so the pause is
     switched on and the resume is never let through. Arrows, swiping and opening a review are
     untouched. Re-applied whenever Judge.me rebuilds the slider (e.g. after a filter). Works on
     its own, whether or not the breakdown loads. */
  function holdSliderStill(widget) {
    widget.addEventListener('mouseleave', (e) => {
      if (e.target.closest && e.target.closest('.jdgm-carousel-review-list')) e.stopPropagation();
    }, true);
    const held = new WeakSet();
    const hold = () => {
      const list = widget.querySelector('.jdgm-carousel-review-list');
      const carousel = list && list.querySelector('.jm-carousel');
      if (!carousel || held.has(carousel)) return;
      held.add(carousel);
      for (const el of [list, carousel, ...carousel.querySelectorAll('.jm-carousel__viewport, .jm-carousel__track')]) {
        el.dispatchEvent(new MouseEvent('mouseenter'));
      }
    };
    hold();
    new MutationObserver(hold).observe(widget, { childList: true, subtree: true });
  }

  /* Judge.me's sort menu has seven options and no Free-plan setting to choose them; keep the four
     that matter. Re-applied if Judge.me redraws the menu. Nothing else about the control changes. */
  const SORT_HIDE = ['pictures_first', 'videos_first', 'most_helpful'];
  function trimSortMenu(widget) {
    const trim = () => {
      for (const sel of widget.querySelectorAll('select.jm-sort-filter__select')) {
        for (const o of [...sel.options]) if (SORT_HIDE.includes(o.value) && !o.selected) o.remove();
      }
    };
    trim();
    new MutationObserver(trim).observe(widget, { childList: true, subtree: true });
  }

  /* Star-only ratings imported from AliExpress have no words, so Judge.me stores the product title
     ("The Facet") as their text. On cards and in the opened review, hide the text when it is exactly
     the product title; stars, initials and date stay, and real written reviews never match. The
     opened review lives in a modal outside the widget, so the whole page is watched (batched). */
  function hideGeneratedText(widget) {
    const norm = (t) => (t || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const title = norm(widget.getAttribute('data-product-title'));
    if (!title) return;
    let queued = false;
    const mark = () => {
      queued = false;
      for (const body of document.querySelectorAll('.jdgm-widget .jm-review-content__body, .jdgm-modal .jm-review-content__body')) {
        const item = body.closest('.jm-review-item') || body.parentElement;
        const generated = norm(body.textContent) === title;
        if (item.classList.contains('kh-rating-only') !== generated) item.classList.toggle('kh-rating-only', generated);
      }
    };
    mark();
    new MutationObserver(() => { if (!queued) { queued = true; requestAnimationFrame(mark); } })
      .observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  /* Judge.me's review photo strip (every photo from this product's reviews) shows a "Scroll thumbnails
     right" arrow even when all the photos already fit. Mark a strip that fits; CSS hides its arrows.
     Re-checked when the strip or the window changes size. */
  function markMediaStrip(widget) {
    const seen = new WeakSet();
    const check = (grid) => {
      const c = grid.querySelector('.jm-media-grid__container');
      if (c) grid.classList.toggle('kh-media-fits', c.scrollWidth <= c.clientWidth + 1);
    };
    const ro = new ResizeObserver((entries) => entries.forEach((e) => check(e.target.closest('.jm-media-grid'))));
    const scan = () => {
      for (const grid of widget.querySelectorAll('.jm-media-grid')) {
        check(grid);
        const c = grid.querySelector('.jm-media-grid__container');
        if (c && !seen.has(c)) { seen.add(c); ro.observe(c); }
      }
    };
    scan();
    new MutationObserver(scan).observe(widget, { childList: true, subtree: true });
  }

  async function init(widget) {
    const productId = widget.getAttribute('data-product-id');
    if (!productId) return;
    const header = await until(() => widget.querySelector('.jm-review-widget__header'), WAIT_MS);
    if (!header) return;
    if (!filtersButton(widget)) return;
    const hist = await loadHistogram(productId);
    if (!hist) return;

    const box = build(hist);
    const live = box.querySelector('.kh-rbars__live');
    let active = null; // rating the bar applied, or one the shopper picked in Judge.me's panel
    let busy = false;

    const place = () => {
      const h = widget.querySelector('.jm-review-widget__header');
      if (!h) return;
      const first = h.firstElementChild;
      if (box.parentElement !== h || box.previousElementSibling !== first) first ? first.after(box) : h.prepend(box);
    };
    const show = (rating, announce) => {
      active = rating;
      widget.classList.toggle('kh-rbars-active', rating !== null);
      for (const b of box.querySelectorAll('.kh-rbars__row')) b.setAttribute('aria-pressed', String(Number(b.dataset.rating) === rating));
      box.classList.toggle('kh-rbars--filtered', rating !== null);
      if (announce) live.textContent = rating === null ? 'Showing all reviews' : `Showing ${rating}-star reviews`;
    };

    // Judge.me's "Only Pictures" sort is really a filter: with it on, a rating whose reviews have no
    // photos shows "No items found" although the bar counts every review of that rating. A rating
    // row therefore switches the sort back to the default first, and choosing "Only Pictures" clears
    // a rating filter, so the counts always describe the list underneath.
    const sortSelect = () => widget.querySelector('select.jm-sort-filter__select');
    const PICTURES = 'with_pictures';
    async function leavePicturesSort() {
      const sel = sortSelect();
      if (!sel || sel.value !== PICTURES || ![...sel.options].some((o) => o.value === 'created_at')) return;
      // Back to the product's default sort: "Written first" where it exists (The Glide), else Most Recent.
      sel.value = [...sel.options].some((o) => o.value === 'kh_written_first') ? 'kh_written_first' : 'created_at';
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      await sleep(400);
    }
    // Judge.me answers a sort change during a rating filter with two requests at once (rating + photos,
    // and photos alone) and shows whichever arrives last, so the list could end up empty. The change
    // is held back (capture phase, before Judge.me sees it), the rating is cleared, and then the same
    // change is handed to Judge.me on its own.
    let passing = false;
    widget.addEventListener('change', async (e) => {
      if (passing || !e.target.matches || !e.target.matches('select.jm-sort-filter__select') || e.target.value !== PICTURES) return;
      if (active === null && !widget.querySelector('input[name="rating-filter"]:checked')) return;
      e.stopPropagation();
      busy = true;
      try {
        await clear();
        show(null, true);
        await sleep(700); // let Judge.me's unfiltered reload land first
      } finally {
        busy = false;
      }
      // Clearing redraws the sort menu at Judge.me's own value, so the choice is set again.
      const sel = sortSelect();
      if (!sel || ![...sel.options].some((o) => o.value === PICTURES)) return;
      sel.value = PICTURES;
      passing = true;
      try { sel.dispatchEvent(new Event('change', { bubbles: true })); } finally { passing = false; }
    }, true);

    async function apply(rating) {
      await leavePicturesSort();
      if (!panelOpen(widget)) {
        filtersButton(widget)?.click();
        await until(() => panelOpen(widget), 2000);
      }
      const input = await until(() => widget.querySelector(`input[name="rating-filter"][value="${rating}"]`), 2000);
      if (!input) {
        if (panelOpen(widget)) filtersButton(widget)?.click();
        return false;
      }
      if (!input.checked) (input.closest('label') || input).click();
      return true;
    }
    async function clear() {
      if (!panelOpen(widget)) return true;
      const btn = widget.querySelector('.jm-filter-panel__clear-btn');
      if (btn && !btn.classList.contains('jm-filter-panel__clear-btn--hidden')) {
        btn.click();
        await sleep(150);
      }
      // closing the panel also clears Judge.me's filter
      if (panelOpen(widget)) filtersButton(widget)?.click();
      return true;
    }

    // Judge.me keeps the slider's scroll position when the list changes, so after a filter is
    // applied or cleared the pinned reviews (always first in the list) could be off screen.
    const rewind = () => {
      const track = widget.querySelector('.jdgm-carousel-review-list .jm-carousel__track');
      if (track && track.scrollLeft !== 0) track.scrollLeft = 0;
    };
    // Judge.me reloads the list and runs its own smooth scroll for ~2 s; the last pass lands after it.
    const rewindAfterReload = () => [600, 1500, 2800].forEach((ms) => setTimeout(rewind, ms));

    box.addEventListener('click', async (e) => {
      const row = e.target.closest('.kh-rbars__row');
      if (!row || busy || row.disabled) return;
      busy = true;
      const rating = Number(row.dataset.rating);
      try {
        if (active === rating) {
          await clear();
          show(null, true);
          rewindAfterReload();
        } else if (await apply(rating)) {
          show(rating, true);
          rewindAfterReload();
        } else {
          show(null, false);
        }
      } finally {
        busy = false;
      }
    });

    // Keep the bar in step with Judge.me's own controls (its filter re-renders the list, which
    // this observer sees; the radios themselves fire no usable event).
    const sync = () => {
      if (busy) return;
      const checked = panelOpen(widget) ? widget.querySelector('input[name="rating-filter"]:checked') : null;
      const rating = checked ? Number(checked.value) : null;
      if (rating === active) return;
      show(rating, false);
      widget.classList.remove('kh-rbars-active'); // chosen in Judge.me's panel: leave its chips visible
    };
    const watch = new MutationObserver(() => {
      if (!box.isConnected || box.previousElementSibling !== widget.querySelector('.jm-review-widget__header')?.firstElementChild) place();
      sync();
    });
    widget.addEventListener('click', (e) => {
      if (e.target.closest && e.target.closest('.jm-filter-panel')) setTimeout(sync, 300);
    });

    place();
    watch.observe(widget, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
  }

  async function start() {
    const widget = await until(() => document.querySelector('.jdgm-widget.jdgm-review-widget[data-product-id]'), WAIT_MS, 250);
    if (!widget) return;
    try { holdSliderStill(widget); } catch (e) { /* leave Judge.me's autoplay as it is */ }
    try { trimSortMenu(widget); } catch (e) { /* leave the menu as it is */ }
    try { hideGeneratedText(widget); } catch (e) { /* leave review text as it is */ }
    try { markMediaStrip(widget); } catch (e) { /* leave the photo strip's arrows as they are */ }
    init(widget).catch(() => {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
